import { CheckCircle, CircleOutlined, ErrorOutline, NotInterested } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography
} from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { VehicleDto, VehicleLockStatusDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { useEffect, useState } from 'react'
import { useDebounce } from 'utils'

interface ChecklistItemProps {
  checked?: boolean
  disabled?: boolean
  error?: boolean
  label: string
}

function ChecklistItem({ checked, disabled, error, label }: ChecklistItemProps) {
  return (
    <Grid item xs={12} container spacing={1} alignItems='center'>
      <Grid item>
        {error ? (
          <ErrorOutline color='error' fontSize='small' />
        ) : disabled ? (
          <NotInterested color='disabled' fontSize='small' />
        ) : checked ? (
          <CheckCircle color='success' fontSize='small' />
        ) : (
          <CircleOutlined color='disabled' fontSize='small' />
        )}
      </Grid>

      <Grid item>
        <Typography>{label}</Typography>
      </Grid>
    </Grid>
  )
}

interface VehicleLockStatusDialogProps {
  onClose: () => void
  open: boolean
  vehicle: VehicleDto
}

export default function VehicleLockStatusDialog({ onClose, open, vehicle }: VehicleLockStatusDialogProps) {
  const [vehicleLockStatus, setVehicleLockStatus] = useState<VehicleLockStatusDto>(new VehicleLockStatusDto())
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicleLockStatus: true
  })

  const getVehicleLockStatus = useDebounce((vehicleGuid: string) => {
    setLoadingState('isGettingVehicleLockStatus', true)
    controllers
      .getVehicleLockStatus(vehicleGuid)
      .then(response => {
        setVehicleLockStatus(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicleLockStatus', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (open && vehicle && vehicle.vehicleGuid) {
      getVehicleLockStatus(vehicle.vehicleGuid)
    }

    return () => {
      setLoadingState('isGettingVehicleLockStatus', true)
    }
  }, [open, vehicle, getVehicleLockStatus])

  return (
    <Dialog open={open} maxWidth='md' fullWidth>
      <DialogTitle>Lock Status</DialogTitle>

      <DialogContent>
        {loadingState.isGettingVehicleLockStatus && (
          <Grid item container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
            <Grid item>
              <CircularProgress color='primary' size={50} />
            </Grid>
          </Grid>
        )}

        {!loadingState.isGettingVehicleLockStatus && (
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={12} sm={6}>
              <LabelField label='VIN' value={vehicleLockStatus.vin} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LabelField label='Exporter' value={vehicleLockStatus.exporterCompany?.name} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LabelField label='Year' value={vehicleLockStatus.year} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LabelField label='Buyer' value={vehicleLockStatus.buyerCompany?.name} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LabelField label='Make' value={vehicleLockStatus.make} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LabelField label='Vehicle Destination Company' value={vehicleLockStatus.vehicleDestinationCompany?.name} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LabelField label='Model' value={vehicleLockStatus.model} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LabelField label='Inbound Transporter Company' value={vehicleLockStatus.inboundTripTransporterCompany?.name} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LabelField label='Appendix B Vehicle' value={vehicleLockStatus.isAppendixBVehicle ? 'Yes' : 'No'} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LabelField label='Special Bond # Required' value={vehicleLockStatus.isSpecialBondRequired ? 'Yes' : 'No'} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} container spacing={1}>
              <ChecklistItem checked={vehicleLockStatus.hasAmountUSD} label='Amount (USD) is set' />

              <ChecklistItem
                checked={vehicleLockStatus.amountUSDIsGreaterThanZero}
                label='Amount (USD) is greater than $0.00'
              />

              <ChecklistItem checked={vehicleLockStatus.hasExporterMileage} label='Exporter Mileage is set' />

              <ChecklistItem
                checked={vehicleLockStatus.hasExporterMileageUnitOfMeasurement}
                label='Exporter Mileage Unit of Measurement (Miles/Kilometers) is set'
              />

              <ChecklistItem checked={vehicleLockStatus.hasWeightGVWR} label='GVWR is set' />

              <ChecklistItem checked={vehicleLockStatus.hasWeightGAWRFR} label='GAWR FR is set' />

              <ChecklistItem checked={vehicleLockStatus.hasWeightGAWRRR} label='GAWR RR is set' />

              <ChecklistItem checked={vehicleLockStatus.hasTitleProcessingState} label='Title Processing State is set' />

              <ChecklistItem checked={vehicleLockStatus.hasTitleDestinationCompany} label='Title Destination Company is set' />

              <ChecklistItem
                checked={vehicleLockStatus.hasTitleDestinationCompanyAddress}
                label='Title Destination Address is set'
              />

              <ChecklistItem
                checked={vehicleLockStatus.hasVehicleDestinationCompany}
                label='Vehicle Destination Company is set'
              />

              <ChecklistItem
                checked={vehicleLockStatus.hasVehicleDestinationCompanyAddress}
                label='Vehicle Destination Address is set'
              />

              <ChecklistItem checked={vehicleLockStatus.hasConvertCluster} label='Convert Cluster is set' />

              <ChecklistItem
                checked={vehicleLockStatus.hasManufacturerLabelImageExporter}
                label='Manufacturer Label Image (Exporter) has been uploaded'
              />

              <ChecklistItem
                checked={vehicleLockStatus.hasTPMSTelltaleImageExporterFileName}
                disabled={!vehicle.isAppendixBVehicle}
                label='This is an Appendix B Vehicle and the TPMS Label Image (Exporter) has been uploaded'
              />

              <ChecklistItem
                checked={vehicleLockStatus.amountUSDIsGreaterThanOrEqualToBondThresholdAndHasSpecialBondNumber}
                disabled={!vehicleLockStatus.isSpecialBondRequired}
                label="Vehicle's Amount (USD) is greater than or equal to Bond Threshold and Special Bond # is set"
              />

              <ChecklistItem
                checked={vehicleLockStatus.amountUSDIsLessThanBondThresholdAndBondNumberAvailable}
                disabled={vehicleLockStatus.isSpecialBondRequired}
                error={
                  vehicleLockStatus.isSpecialBondRequired === false &&
                  vehicleLockStatus.amountUSDIsLessThanBondThresholdAndBondNumberAvailable === false
                }
                label="Vehicle's Amount (USD) is less than Bond Threshold and a bond number is available to be assigned."
              />
            </Grid>

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                || - The criteria in this dialog will show an empty circle (instead of a green checkmark icon) if: || - Amount
                (USD) is not set. || - Amount (USD) is zero. || - Exporter Mileage is not set. || - Exporter Mileage unit of
                measure (Miles/Kilometers) is not set. || - GVWR is not set. || - GAWR FR is not set. || - GAWR RR is not set.
                || - Title Processing State is not set. || - Title Destination Company is not set. || - Title Destination
                Address is not set. || - Vehicle Destination Company is not set. || - Vehicle Destination Address is not set. ||
                - Convert Cluster is not set. || - Manufacturer Label Image (Exporter) is missing. || - TPMS Label Image
                (Exporter) is missing when vehicle year/make/model combination is in the Appendix B list. || - The Amount (USD)
                is below Bond Threshold and there is no Bond number available (the last bond number from the current bond block
                has been consumed and there is no new bond range to begin pulling bond numbers from) to assign. || - The Amount
                (USD) is greater than or equal to Bond Threshold and Special Bond# is not set. || If in the dialog header
                Appendix B Vehicle = No, the criteria item “This is an Appendix B vehicle and the TPMS Label Image (Exporter)
                has been uploaded” will show “N/A” instead of a green checkmark or empty circle. || If in the dialog header
                Special Bond# Required = No, the criteria item “Vehicle’s Amount (USD) is greater than or equal to Bond
                Threshold and Special Bond# is set.” will show “N/A” instead of a green checkmark or empty circle. || If in the
                dialog header Special Bond# Required = Yes, the criteria item “Vehicle’s Amount (USD) is less than Bond
                Threshold and a bond number is available to be assigned.” will show “N/A” instead of a green checkmark or empty
                circle. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button color='inherit' onClick={onClose} variant='text'>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  )
}
